import { useCallback, useEffect, useRef } from 'react';
import {
  GatewayClient,
  GatewayError,
  type AgentEvent,
  type AgentInfo,
  type Attachment,
  type ChatEvent,
  type ResetConfirmRequiredPayload,
  type SignDeviceContext,
} from '@clawlet/gateway-client';
import { getOrCreateDevice, signChallenge } from '../utils/deviceIdentity';
import { gatewayRef } from '../utils/gatewayRef';
import {
  agentIdFromSessionKey,
  type ChatMessage,
  type ImageContentBlock,
  isCurrentHistoryRequest,
  isSubagentSessionKey,
  type ThinkingBlock,
  type ToolCallEntry,
  useAppStore,
} from '../stores/appStore';
import { useSettingsStore } from '../stores/settingsStore';
import {
  handleSharedAgentEvent,
  resetAllSharedAgentTurnState,
  resetSharedAgentTurnState,
  shouldApplySubagentError,
} from '../utils/agentEventHandler';
import { toFriendlyErrorMessage } from '../utils/friendlyErrors';
import {
  buildApiUrl,
  getGatewayUrlForCurrentServer,
  persistServerOriginFromGatewayUrl,
} from '../utils/serverOrigin';
import {
  AGENT_LIST_REFRESH_INTERVAL_MS,
  shouldAutoRefreshAgentList,
} from '../utils/agentListRefresh';
import { filterAgentsForAllowedList } from '../utils/agentVisibility';
import { isTeamSession, notifyTeamDelta, notifyTeamDone } from '../utils/teamSessionRegistry';

/** Extract pipeline ID from a pipeline session key, or null if not a pipeline session. */
function extractPipelineId(sessionKey: string): string | null {
  const parts = sessionKey.split(':');
  const sessionId = parts.length >= 3 ? parts.slice(2).join(':') : '';
  return sessionId.startsWith('pipeline-') ? sessionId : null;
}

const INITIAL_RECONNECT_DELAY_MS = 800;
const MAX_RECONNECT_DELAY_MS = 15_000;
const RECONNECT_FACTOR = 1.7;
const SUCCESS_BANNER_MS = 2_000;

function reconnectDelayForAttempt(attempt: number): number {
  let delay = INITIAL_RECONNECT_DELAY_MS;

  for (let index = 1; index < attempt; index += 1) {
    delay = Math.min(MAX_RECONNECT_DELAY_MS, Math.round(delay * RECONNECT_FACTOR));
  }

  return delay;
}

function looksLikeAuthError(error: GatewayError): boolean {
  const code = error.details?.code ?? error.code ?? '';
  const message = error.message.toLowerCase();

  return (
    code === 'PAIRING_REQUIRED' ||
    code === 'UNAUTHORIZED' ||
    code === 'AUTH_REQUIRED' ||
    code === 'INVALID_TOKEN' ||
    message.includes('unauthorized') ||
    message.includes('invalid token') ||
    message.includes('authentication')
  );
}

function friendlyConnectionMessage(error: Error): string {
  if (error instanceof GatewayError && error.details?.code === 'PAIRING_REQUIRED') {
    return 'This device needs permission before it can connect again.';
  }

  const message = error.message.toLowerCase();
  if (
    (error instanceof GatewayError && looksLikeAuthError(error)) ||
    message.includes('unauthorized') ||
    message.includes('invalid token') ||
    message.includes('authentication')
  ) {
    return 'We could not reconnect with this link. Please try again or ask for a fresh invite.';
  }

  if (message.includes('websocket') || message.includes('socket') || message.includes('network')) {
    return 'Connection lost. Trying to reconnect…';
  }

  return 'Connection lost. Trying to reconnect…';
}

function friendlyDisconnectMessage(reason: string | null | undefined): string {
  if (!reason) {
    return 'Connection lost. Trying to reconnect…';
  }

  const normalized = reason.toLowerCase();
  if (
    normalized === 'connect failed' ||
    normalized === 'socket closed' ||
    normalized === 'abnormal' ||
    normalized === 'websocket error' ||
    normalized === 'client disconnect'
  ) {
    return 'Connection lost. Trying to reconnect…';
  }

  if (normalized.includes('pair')) {
    return 'This device needs permission before it can connect again.';
  }

  return 'Connection lost. Trying to reconnect…';
}

function sessionKeyFromSession(session: any): string | null {
  if (typeof session?.sessionKey === 'string') return session.sessionKey;
  if (typeof session?.key === 'string') return session.key;
  if (typeof session?.id === 'string') return session.id;
  return null;
}

function visibleAgents(agents: AgentInfo[]): AgentInfo[] {
  return agents.filter((agent) => agent.identity != null);
}

export function personalSessionKey(agentId: string, displayName: string): string {
  const fallbackDisplayName = typeof window !== 'undefined'
    ? (
        window.localStorage.getItem('clawlet.displayName') ??
        window.localStorage.getItem('displayName') ??
        window.localStorage.getItem('clawlet.userName') ??
        ''
      ).trim()
    : '';
  const effectiveDisplayName = displayName.trim() || fallbackDisplayName;
  const sanitized = effectiveDisplayName.toLowerCase().replace(/[^a-z0-9]/g, '-');
  return `agent:${agentId}:${sanitized || 'main'}`;
}

function mainSessionKeyForAgent(agentId: string): string {
  const displayName = useSettingsStore.getState().settings.general.displayName;

  return personalSessionKey(agentId, displayName);
}

function sessionMatchesAgent(session: any, agentId: string): boolean {
  const key = sessionKeyFromSession(session);
  if (!key) {
    return false;
  }
  return agentIdFromSessionKey(key) === agentId;
}

function pickPreferredSessionKeyForAgent(sessions: any[], agentId: string): string | null {
  const agentSessions = sessions.filter((session) => sessionMatchesAgent(session, agentId));
  const preferredKey = mainSessionKeyForAgent(agentId);
  const preferredSession = agentSessions.find(
    (session) => sessionKeyFromSession(session) === preferredKey,
  );

  if (preferredSession) {
    return preferredKey;
  }

  if (useAppStore.getState().userRole === 'member') {
    return null;
  }

  const preferred = agentSessions.find((session) => session?.kind === 'main') ?? agentSessions[0];
  return sessionKeyFromSession(preferred);
}

function normalizeSelectedAgentId(
  allAgents: AgentInfo[],
  selectedAgentId: string | null,
  fallbackAgentId: string,
  allowedAgents?: string[],
): string {
  let pickable = visibleAgents(allAgents);

  pickable = filterAgentsForAllowedList(pickable, allowedAgents);

  if (pickable.length === 0) {
    return fallbackAgentId;
  }

  if (selectedAgentId && pickable.some((agent) => agent.id === selectedAgentId)) {
    return selectedAgentId;
  }

  if (pickable.some((agent) => agent.id === fallbackAgentId)) {
    return fallbackAgentId;
  }

  return pickable[0].id;
}

function toMessageRole(role: unknown): ChatMessage['role'] {
  if (role === 'user' || role === 'assistant' || role === 'system') {
    return role;
  }
  return 'assistant';
}

/** Extract plain text from a message content field that may be a string or structured blocks */
function extractTextContent(content: unknown): string {
  if (typeof content === 'string') return content;

  if (Array.isArray(content)) {
    return content
      .filter((block: any) => block?.type === 'text' && typeof block.text === 'string')
      .map((block: any) => block.text)
      .join('\n');
  }

  if (content && typeof content === 'object') {
    const maybeObject = content as Record<string, any>;

    if (typeof maybeObject.content === 'string') {
      return maybeObject.content;
    }

    if (Array.isArray(maybeObject.content)) {
      return extractTextContent(maybeObject.content);
    }

    if (typeof maybeObject.text === 'string') {
      return maybeObject.text;
    }
  }

  return '';
}

function extractImageBlocks(content: unknown): ImageContentBlock[] {
  if (Array.isArray(content)) {
    return content.filter(
      (block: any) => block?.type === 'image' && block?.source?.type === 'base64',
    );
  }

  if (content && typeof content === 'object') {
    const maybeObject = content as Record<string, any>;
    if (Array.isArray(maybeObject.content)) {
      return extractImageBlocks(maybeObject.content);
    }
  }

  return [];
}

function extractThinkingBlocks(content: unknown): ThinkingBlock[] {
  if (Array.isArray(content)) {
    return content.filter(
      (block: any) =>
        block?.type === 'thinking' && typeof block?.thinking === 'string' && block.thinking.trim().length > 0,
    );
  }

  if (content && typeof content === 'object') {
    const maybeObject = content as Record<string, any>;
    if (Array.isArray(maybeObject.content)) {
      return extractThinkingBlocks(maybeObject.content);
    }
  }

  return [];
}

/** Convert the backend's toolCalls field (from history) into ToolCallEntry[] */
function historyToolCalls(raw: any[] | undefined): ToolCallEntry[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;

  // Find the Agent tool's ID so we can infer parentage for legacy records
  // that don't have parentToolCallId stored yet.
  const agentId = raw.find((tc: any) => tc.name === 'Agent')?.toolCallId
    ?? raw.find((tc: any) => tc.name === 'Agent')?.id;

  return raw.map((tc: any) => {
    const id = tc.toolCallId ?? tc.id ?? '';
    // Explicit parentToolCallId from backend, or infer: non-Agent tools
    // in a message that has an Agent tool are subagent tools.
    const parent = tc.parentToolCallId
      ?? (agentId && tc.name !== 'Agent' ? agentId : undefined);
    return {
      toolCallId: id,
      name: tc.name ?? 'unknown',
      timestamp: Date.now(),
      status: (tc.isError ? 'error' : 'done') as ToolCallEntry['status'],
      params: tc.params,
      result: tc.result,
      parentToolCallId: parent,
      error: tc.isError ? { message: typeof tc.result === 'string' ? tc.result : 'Error' } : undefined,
    };
  });
}

function toHistoryChatMessages(messages: any[]): ChatMessage[] {
  const result: ChatMessage[] = [];
  for (const msg of messages) {
    const msgRole = msg?.role;
    if (msgRole === 'tool' || msgRole === 'toolResult' || msgRole === 'tool_result') continue;

    const textContent = extractTextContent(msg?.content).trim();
    const imageBlocks = extractImageBlocks(msg?.content);
    const toolCalls = historyToolCalls(msg?.toolCalls);
    const hasContent = textContent || imageBlocks.length > 0 || (toolCalls && toolCalls.length > 0);

    if (!hasContent) continue;
    if (textContent.startsWith('[System Message]')) continue;

    const thinkingBlocks = extractThinkingBlocks(msg?.content);
    const injectedContext =
      typeof msg?.injectedContext === 'string' && msg.injectedContext.trim().length > 0
        ? msg.injectedContext
        : undefined;

    result.push({
      role: toMessageRole(msgRole),
      content: textContent,
      timestamp: msg?.timestamp ? String(msg.timestamp) : undefined,
      isHistory: true,
      images: imageBlocks.length > 0 ? imageBlocks : undefined,
      thinkingBlocks: thinkingBlocks.length > 0 ? thinkingBlocks : undefined,
      toolCalls,
      injectedContext,
      agent: msg?.agent || undefined,
    });
  }
  return result;
}

function catchUpComparableEntries(messages: ChatMessage[]): Array<{ index: number; key: string }> {
  return messages.flatMap((message, index) => {
    if (message.role === 'system' && !message.isHistory) {
      return [];
    }

    return [
      {
        index,
        key: JSON.stringify({
          role: message.role,
          content: message.content,
          images: message.images ?? [],
        }),
      },
    ];
  });
}

function mergeCatchUpMessages(existing: ChatMessage[], fetched: ChatMessage[]): ChatMessage[] {
  if (fetched.length === 0) {
    return existing;
  }

  const existingEntries = catchUpComparableEntries(existing);
  const fetchedEntries = catchUpComparableEntries(fetched);

  if (fetchedEntries.length === 0) {
    return existing;
  }

  const maxOverlap = Math.min(existingEntries.length, fetchedEntries.length);
  for (let overlap = maxOverlap; overlap > 0; overlap -= 1) {
    let matches = true;

    for (let offset = 0; offset < overlap; offset += 1) {
      const existingKey = existingEntries[existingEntries.length - overlap + offset]?.key;
      const fetchedKey = fetchedEntries[offset]?.key;
      if (existingKey !== fetchedKey) {
        matches = false;
        break;
      }
    }

    if (!matches) {
      continue;
    }

    const appendFromIndex =
      overlap >= fetchedEntries.length ? fetched.length : fetchedEntries[overlap]?.index ?? fetched.length;

    return appendFromIndex >= fetched.length ? existing : [...existing, ...fetched.slice(appendFromIndex)];
  }

  const existingKeys = new Set(existingEntries.map((entry) => entry.key));
  const missing = fetched.filter((message) => {
    const entry = catchUpComparableEntries([message])[0];
    return entry ? !existingKeys.has(entry.key) : false;
  });

  return missing.length > 0 ? [...existing, ...missing] : existing;
}

const DEVICE_TOKEN_KEY = 'clawlet-device-token';

function loadDeviceToken(): string | null {
  try {
    return localStorage.getItem(DEVICE_TOKEN_KEY);
  } catch {
    return null;
  }
}

function saveDeviceToken(token: string): void {
  try {
    localStorage.setItem(DEVICE_TOKEN_KEY, token);
  } catch {
    // localStorage not available
  }
}

const INVITE_TOKEN_KEY = 'clawlet.inviteToken';
const APPROVE_DEVICE_URL = '/api/invites/approve-device';
const RECONNECT_CATCH_UP_DELAY_MS = 500;
const RECONNECT_CATCH_UP_HISTORY_LIMIT = 100;
const ACTIVE_SESSION_CATCH_UP_INTERVAL_MS = 30_000;

function getInviteToken(): string | null {
  try {
    return localStorage.getItem(INVITE_TOKEN_KEY);
  } catch {
    return null;
  }
}

function clearInviteToken(): void {
  try {
    localStorage.removeItem(INVITE_TOKEN_KEY);
  } catch {
    // localStorage not available
  }
}

function resolvePluginGatewayUrl(url: string): string {
  return getGatewayUrlForCurrentServer(url);
}

async function attemptAutoApprove(requestId: string): Promise<boolean> {
  const inviteToken = getInviteToken();
  if (!inviteToken) {
    console.debug('[useGateway] No invite token stored, cannot auto-approve');
    return false;
  }

  try {
    const response = await fetch(buildApiUrl(APPROVE_DEVICE_URL, useAppStore.getState().gatewayUrl), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: inviteToken, request_id: requestId }),
    });

    const data = await response.json();
    if (data.approved) {
      console.debug('[useGateway] Device auto-approved successfully');
      clearInviteToken();
      return true;
    }
    console.warn('[useGateway] Auto-approve failed:', data.error);
    return false;
  } catch (error) {
    console.error('[useGateway] Auto-approve request failed:', error);
    return false;
  }
}

export function useGateway() {
  const clientRef = useRef<GatewayClient | null>(null);
  const verboseEnabledSessionsRef = useRef<Set<string>>(new Set());
  const reasoningEnabledSessionsRef = useRef<Set<string>>(new Set());
  const manualDisconnectRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  const lastConnectionIssueRef = useRef<string | null>(null);
  const successBannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoRefreshConnected = useAppStore((state) => state.connected);
  const userRole = useAppStore((state) => state.userRole);
  const allowedAgents = useAppStore((state) => state.allowedAgents);

  const clearSuccessBannerTimer = useCallback(() => {
    if (successBannerTimerRef.current) {
      clearTimeout(successBannerTimerRef.current);
      successBannerTimerRef.current = null;
    }
  }, []);

  const showRetryBanner = useCallback((message: string) => {
    const attempt = reconnectAttemptRef.current + 1;
    reconnectAttemptRef.current = attempt;

    useAppStore.getState().setReconnectState({
      reconnectStatus: 'retrying',
      reconnectAttempt: attempt,
      nextReconnectAt: Date.now() + reconnectDelayForAttempt(attempt),
      reconnectMessage: message,
    });
  }, []);

  const enableSessionVerbose = useCallback(async (sessionKey: string) => {
    const client = clientRef.current;
    if (!client || !client.connected || !sessionKey) {
      return;
    }

    if (verboseEnabledSessionsRef.current.has(sessionKey)) {
      return;
    }

    console.debug('[useGateway] enableSessionVerbose: sending sessions.patch for', sessionKey);

    try {
      await client.setSessionVerbose(sessionKey, 'full');
      verboseEnabledSessionsRef.current.add(sessionKey);
    } catch (error) {
      // Older gateways may not support verboseLevel. Keep chat functional.
      console.debug('[useGateway] Unable to set verbose mode:', error);
    }

    // Enable reasoning stream for this session via sessions.patch
    if (!reasoningEnabledSessionsRef.current.has(sessionKey)) {
      try {
        await client.request('sessions.patch', { key: sessionKey, reasoningLevel: 'stream' });
        reasoningEnabledSessionsRef.current.add(sessionKey);
      } catch (error) {
        console.debug('[useGateway] Unable to enable reasoning stream:', error);
      }
    }
  }, []);

  const loadHistoryForSession = useCallback(
    async (sessionKey: string, force = false) => {
      const client = clientRef.current;
      if (!client || !client.connected || !sessionKey) {
        return;
      }

      // Mark history as loading immediately to prevent welcome screen flash
      useAppStore.getState().setHistoryLoading(sessionKey, true);
      const startedHistoryVersion = useAppStore.getState().historyInvalidationVersion[sessionKey] ?? 0;

      try {
        await enableSessionVerbose(sessionKey);

        // When not forced, skip fetching if messages are already in the store.
        // This avoids replacing live-streamed messages during a session switch
        // and reduces unnecessary network requests.
        // When forced (e.g. on first connect / browser refresh), always fetch
        // fresh history from the server to guarantee an up-to-date view.
        if (!force) {
          const existing = useAppStore.getState().messages[sessionKey];
          if (existing && existing.length > 0) {
            return;
          }
        }

        // Pipeline sessions: fetch merged history from both agents
        const pipelineId = extractPipelineId(sessionKey);
        let messages;
        if (pipelineId) {
          try {
            ({ messages } = await client.getPipelineHistory(pipelineId));
          } catch (err) {
            console.warn('[useGateway] pipeline.history failed, falling back to chat.history:', err);
            ({ messages } = await client.getHistory(sessionKey, 1000));
          }
        } else {
          ({ messages } = await client.getHistory(sessionKey, 1000));
        }
        const historyMessages = toHistoryChatMessages(messages);
        if (!isCurrentHistoryRequest(
          useAppStore.getState().historyInvalidationVersion,
          sessionKey,
          startedHistoryVersion,
        )) {
          return;
        }

        if (historyMessages.length > 0) {
          if (force) {
            // Forced reload: always replace with fresh server history.
            // Preserve client-only fields (injectedContext) from existing messages.
            const existing = useAppStore.getState().messages[sessionKey];
            if (existing && existing.length > 0) {
              let ei = 0;
              for (const hm of historyMessages) {
                while (ei < existing.length && existing[ei].role !== hm.role) ei++;
                if (ei < existing.length && existing[ei].injectedContext) {
                  hm.injectedContext = existing[ei].injectedContext;
                }
                ei++;
              }
            }
            useAppStore.getState().replaceMessages(sessionKey, historyMessages);
          } else {
            // Non-forced: only replace if the store is still empty (race guard).
            const existingAfterFetch = useAppStore.getState().messages[sessionKey];
            const hasExistingMessages = existingAfterFetch && existingAfterFetch.length > 0;
            if (!hasExistingMessages) {
              useAppStore.getState().replaceMessages(sessionKey, historyMessages);
            }
          }
        }
      } catch (error) {
        console.error('[useGateway] Failed to load chat history:', error);
        useAppStore.getState().setConnectionError(toFriendlyErrorMessage(error, 'chat-history'));
      } finally {
        useAppStore.getState().setHistoryLoading(sessionKey, false);
      }
    },
    [enableSessionVerbose],
  );

  const catchUpHistoryForSession = useCallback(
    async (sessionKey: string) => {
      const client = clientRef.current;
      if (!client || !client.connected || !sessionKey) {
        return;
      }
      const startedHistoryVersion = useAppStore.getState().historyInvalidationVersion[sessionKey] ?? 0;

      await enableSessionVerbose(sessionKey);
      await new Promise<void>((resolve) => {
        setTimeout(resolve, RECONNECT_CATCH_UP_DELAY_MS);
      });

      if (clientRef.current !== client || !client.connected) {
        return;
      }
      if (!isCurrentHistoryRequest(
        useAppStore.getState().historyInvalidationVersion,
        sessionKey,
        startedHistoryVersion,
      )) {
        return;
      }

      try {
        const { messages } = await client.getHistory(sessionKey, RECONNECT_CATCH_UP_HISTORY_LIMIT);
        if (!isCurrentHistoryRequest(
          useAppStore.getState().historyInvalidationVersion,
          sessionKey,
          startedHistoryVersion,
        )) {
          return;
        }
        const fetchedMessages = toHistoryChatMessages(messages);
        if (fetchedMessages.length === 0) {
          return;
        }

        const store = useAppStore.getState();
        const existingMessages = store.messages[sessionKey] ?? [];
        const mergedMessages = mergeCatchUpMessages(existingMessages, fetchedMessages);

        if (mergedMessages.length !== existingMessages.length) {
          const appendedMessages = mergedMessages.slice(existingMessages.length);
          console.debug(
            '[useGateway] Reconnect catch-up appended',
            appendedMessages.length,
            'message(s) for',
            sessionKey,
          );
          store.replaceMessages(sessionKey, mergedMessages);
          if (appendedMessages.some((message) => message.role === 'assistant')) {
            store.setStreamingMessage(sessionKey, null);
            store.setThinking(sessionKey, false);
            store.setThinkingContent(sessionKey, null);
            store.clearToolCalls(sessionKey);
            resetSharedAgentTurnState(sessionKey);
          }
        }
      } catch (error) {
        console.warn('[useGateway] Failed to catch up recent history:', error);
      }
    },
    [enableSessionVerbose],
  );

  const refreshSessions = useCallback(
    async () => {
      const client = clientRef.current;
      if (!client || !client.connected) {
        return;
      }

      const state = useAppStore.getState();

      try {
        const sessions = await client.listSessions();
        state.setSessions(sessions);

        // Reconcile subagent status based on active sessions
        const activeSessionKeys = sessions
          .map((s) => sessionKeyFromSession(s))
          .filter((k): k is string => k !== null);
        const sessionUpdates: Record<string, number> = {};
        for (const session of sessions) {
          const key = sessionKeyFromSession(session);
          if (key && session.updatedAt) {
            sessionUpdates[key] = new Date(session.updatedAt).getTime();
          }
        }
        state.reconcileSubagents(activeSessionKeys, sessionUpdates);

        const latestState = useAppStore.getState();
        const selectedAgentId = latestState.selectedAgentId ?? 'main';
        const fallbackMainKey = mainSessionKeyForAgent(selectedAgentId);
        const activeSessionKey = latestState.activeSessionKey;

        const activeIsInList = activeSessionKey
          ? sessions.some((session) => sessionKeyFromSession(session) === activeSessionKey)
          : false;

        const activeMatchesSelectedAgent =
          activeSessionKey != null &&
          agentIdFromSessionKey(activeSessionKey) === selectedAgentId;

        const desiredActiveKey =
          activeMatchesSelectedAgent && (activeIsInList || activeSessionKey === fallbackMainKey)
            ? (activeSessionKey ?? fallbackMainKey)
            : (pickPreferredSessionKeyForAgent(sessions, selectedAgentId) ?? fallbackMainKey);

        if (desiredActiveKey !== activeSessionKey) {
          useAppStore.getState().setHistoryLoading(desiredActiveKey, true);
          state.setActiveSessionKey(desiredActiveKey);
          await loadHistoryForSession(desiredActiveKey);
        }
      } catch (error) {
        console.error('[useGateway] Failed to refresh sessions:', error);
        state.setConnectionError(toFriendlyErrorMessage(error, 'chat-history'));
      }
    },
    [loadHistoryForSession],
  );

  /** Fetch only the last assistant message from history as fallback */
  const fetchLastAssistantMessage = useCallback(async (sessionKey: string) => {
    const client = clientRef.current;
    if (!client || !client.connected || !sessionKey) return;
    const startedHistoryVersion = useAppStore.getState().historyInvalidationVersion[sessionKey] ?? 0;

    try {
      // Fetch recent history and find the last assistant message
      const { messages } = await client.getHistory(sessionKey, 10);
      if (!isCurrentHistoryRequest(
        useAppStore.getState().historyInvalidationVersion,
        sessionKey,
        startedHistoryVersion,
      )) {
        return;
      }
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const msg = messages[i];
        if (msg.role === 'assistant') {
          const text = extractTextContent(msg.content).trim();
          const images = extractImageBlocks(msg.content);
          if (text || images.length > 0) {
            useAppStore.getState().addMessage(sessionKey, {
              role: 'assistant',
              content: text,
              timestamp: new Date().toISOString(),
              images: images.length > 0 ? images : undefined,
            });
            return;
          }
        }
      }
    } catch (error) {
      console.warn('[useGateway] Failed to fetch last message:', error);
    }
  }, []);

  /** Fetch the last assistant message's usage to get actual context window fill.
   *  Context fill = input_tokens + cache_read + cache_creation from the most recent turn,
   *  which is the total tokens the model "sees" right now (conversation + system prompt). */
  const fetchContextFill = useCallback(async (sessionKey: string) => {
    const client = clientRef.current;
    if (!client || !client.connected || !sessionKey) return;

    try {
      const { messages } = await client.getHistory(sessionKey, 10);

      // Find the last assistant message with usage data.
      // This single turn's input-side total = actual context window fill,
      // because each turn's input already contains the full conversation history.
      for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (msg.role !== 'assistant') continue;
        const usage = (msg as any).usage;
        if (!usage || typeof usage !== 'object') continue;
        const input = Number(usage.input ?? usage.input_tokens ?? 0) || 0;
        const cacheRead = Number(usage.cacheRead ?? usage.cache_read_input_tokens ?? 0) || 0;
        const cacheWrite = Number(usage.cacheWrite ?? usage.cache_creation_input_tokens ?? 0) || 0;
        const contextFill = input + cacheRead + cacheWrite;
        if (contextFill > 0) {
          useAppStore.getState().setSessionContextFill(sessionKey, contextFill);
        }
        break;
      }
    } catch (error) {
      console.debug('[useGateway] Failed to fetch context fill:', error);
    }
  }, []);

  /** After a turn completes, fetch history to get thinking blocks (not streamed via WS). */
  const fetchThinkingForLastMessage = useCallback(async (sessionKey: string) => {
    const client = clientRef.current;
    if (!client || !client.connected || !sessionKey) return;
    const startedHistoryVersion = useAppStore.getState().historyInvalidationVersion[sessionKey] ?? 0;

    try {
      const { messages } = await client.getHistory(sessionKey, 1);
      if (!isCurrentHistoryRequest(
        useAppStore.getState().historyInvalidationVersion,
        sessionKey,
        startedHistoryVersion,
      )) {
        return;
      }
      const lastMsg = messages[messages.length - 1];
      if (!lastMsg || lastMsg.role !== 'assistant') return;

      const blocks = extractThinkingBlocks(lastMsg.content);
      if (blocks.length > 0) {
        useAppStore.getState().patchLastMessage(sessionKey, {
          thinkingBlocks: blocks,
        });
      }
    } catch (error) {
      console.debug('[useGateway] Failed to fetch thinking blocks:', error);
    }
  }, []);

  const handleChatEvent = useCallback(
    (event: ChatEvent) => {
      const state = useAppStore.getState();
      const sessionKey = event.sessionKey;
      if (!sessionKey) {
        console.warn('[session-routing] dropping chat event with missing sessionKey', { eventType: event.type, state: event.state });
        return;
      }

      // DEBUG: Log chat event lifecycle
      console.debug(`[handleChatEvent] state=${event.state} sessionKey=${sessionKey} runId=${event.runId} ts=${Date.now()}`);

      // ============================================================
      // Subagent Event Routing - Early Return to prevent corrupting main chat
      // ============================================================
      if (isSubagentSessionKey(sessionKey)) {
        // NOTE: Don't suppress subagent ChatEvents when plugin stream is connected.
        // The plugin stream only carries AgentEvents (lifecycle/assistant/tool/thinking),
        // not ChatEvents (delta/final). Additionally, the plugin filter
        // (sessionKey.startsWith("agent:main:")) drops events for non-main subagents
        // (e.g. agent:coder:subagent:*). Native WS must always handle subagent ChatEvents.

        if (event.state === 'delta') {
          // Skip delta processing — the AgentEvent 'assistant' stream in
          // agentEventHandler.ts already provides real-time streaming text
          // via mergeSubagentStreamingText → setSubagentStreamingText.
          // Processing ChatEvent deltas HERE as well caused duplication:
          // when a ChatEvent delta arrived after AgentEvents had already
          // advanced the store text, the delta handler misidentified the
          // older cumulative text as a "new topic" and appended it.
          state.upsertSubagent(sessionKey);
        } else if (event.state === 'final') {
          const text = extractTextContent(event.message).trim();
          if (text) {
            state.upsertSubagent(sessionKey);
            state.setSubagentStreamingText(sessionKey, text);
          }
          state.markSubagentCompleted(sessionKey);
          resetSharedAgentTurnState(sessionKey, event.runId);
        } else if (event.state === 'error') {
          if (shouldApplySubagentError(sessionKey)) {
            state.markSubagentError(sessionKey);
            resetSharedAgentTurnState(sessionKey, event.runId);
          }
        } else if (event.state === 'aborted') {
          state.markSubagentCompleted(sessionKey);
          resetSharedAgentTurnState(sessionKey, event.runId);
        }
        return;
      }

      if (event.state === 'context_injected') {
        // Store injected context (memory/wiki preamble) for debug display
        const contextContent = extractTextContent(event.message);
        if (contextContent) {
          state.setInjectedContext(sessionKey, contextContent);
          // Attach to the last user message in history
          const msgs = state.messages[sessionKey];
          if (msgs && msgs.length > 0) {
            const lastIdx = msgs.length - 1;
            if (msgs[lastIdx].role === 'user') {
              state.patchLastMessage(sessionKey, { injectedContext: contextContent });
            }
          }
        }
        return;
      }

      if (event.state === 'user') {
        const text = extractTextContent(event.message);
        if (text) {
          const pendingInjectedContext = state.pendingInjectedContext[sessionKey] || undefined;
          state.addMessage(sessionKey, {
            role: 'user',
            content: text,
            timestamp: new Date().toISOString(),
            injectedContext: pendingInjectedContext,
          });
          if (pendingInjectedContext) {
            useAppStore.getState().setInjectedContext(sessionKey, null);
          }
        }
        return;
      }

      if (event.state === 'delta') {
        // Gateway sends accumulated text each time (not incremental chunks).
        // Tool calls are handled internally by Claude CLI — all text across
        // tool-call rounds is accumulated in a single monotonic stream, so
        // we simply overwrite the streaming message.  The chat:final handler
        // commits the final accumulated text as the permanent message.
        const incoming = extractTextContent(event.message);

        // Route team session deltas
        if (isTeamSession(sessionKey) && incoming) {
          notifyTeamDelta(sessionKey, incoming);
        }

        state.setStreamingMessage(sessionKey, incoming);

        // Session reset animation: when first assistant text arrives, clear the animation
        // (Phase 2 -> Phase 3: restore normal)
        const resetPhase = state.sessionResetPhase[sessionKey];
        if (resetPhase && incoming.trim().length > 0) {
          state.clearSessionReset(sessionKey);
        }

        return;
      }

      if (event.state === 'final') {
        const streamedText = (state.streamingMessage[sessionKey] ?? '').trim();

        // Route team session final events — ONLY when there's actual text content.
        // Empty finals (e.g. tool call outputs) must be ignored so the registry
        // entry stays alive across tool-call rounds until the real text arrives.
        if (isTeamSession(sessionKey)) {
          const rawMessage = event.message;
          const teamFinalText = (extractTextContent(rawMessage) || streamedText).trim();
          if (teamFinalText) {
            notifyTeamDone(sessionKey, teamFinalText);
          }
        }

        // event.message may be a string OR a structured content array/object
        const rawMessage = event.message;
        const finalText = extractTextContent(rawMessage).trim();
        const imageBlocks = extractImageBlocks(rawMessage);

        // Extract thinking blocks from the final message content (may be present inline)
        let thinkingBlocks = extractThinkingBlocks(rawMessage);

        // Prefer accumulated streaming content — it's the full thinking text.
        // Extracted blocks from the final message may be redacted/empty.
        const accumulated = state.thinkingContent[sessionKey];
        if (accumulated) {
          thinkingBlocks = [{ type: 'thinking', thinking: accumulated }];
        } else if (thinkingBlocks.length === 0) {
          // No streaming content and no blocks in message — will be fetched
          // from history below via fetchThinkingForLastMessage.
        }

        // Snapshot tool calls before they are cleared.  Finalize any
        // still-"running" entries as "done" — the turn has completed.
        // console.debug('[useGateway] chat.final — tool calls in state:', state.toolCalls[sessionKey]?.length ?? 0);
        const currentToolCalls = state.toolCalls[sessionKey];
        const toolCallsToCommit =
          currentToolCalls && currentToolCalls.length > 0
            ? currentToolCalls.map((tc) =>
                tc.status === 'running'
                  ? { ...tc, status: 'done' as const, completedAt: Date.now() }
                  : tc,
              )
            : undefined;

        state.setStreamingMessage(sessionKey, null);
        state.setThinkingContent(sessionKey, null);

        const displayText = finalText || streamedText;
        if (displayText || imageBlocks.length > 0) {
          state.addMessage(sessionKey, {
            role: 'assistant',
            content: displayText || '',
            timestamp: new Date().toISOString(),
            images: imageBlocks.length > 0 ? imageBlocks : undefined,
            thinkingBlocks: thinkingBlocks.length > 0 ? thinkingBlocks : undefined,
            toolCalls: toolCallsToCommit,
          });

          // Fallback: if no live thinking arrived, try chat.history after the turn completes.
          if (thinkingBlocks.length === 0) {
            void fetchThinkingForLastMessage(sessionKey);
          }
        } else {
          void fetchLastAssistantMessage(sessionKey);
        }

        // Context fill is now updated in real-time via the lifecycle "end" agent event
        // (see agentEventHandler.ts). Fallback fetch is only used on page load/reconnect.

        // Clear ephemeral tool calls now that they are committed on the
        // message.  This closes the live area so the committed message
        // moves to historical rendering with its persisted toggles.
        state.clearToolCalls(sessionKey);
        resetSharedAgentTurnState(sessionKey, event.runId);

        // Refresh session list to update token counts in BuilderStatusBar
        void refreshSessions();
        return;
      }

      if (event.state === 'error') {
        const messageText = extractTextContent(event.message).trim();
        const errorText = event.errorMessage ?? messageText;
        console.error('[useGateway] Chat stream error:', errorText || 'Chat stream error');
        state.setStreamingMessage(sessionKey, null);
        state.setThinking(sessionKey, false);
        state.setThinkingContent(sessionKey, null);
        state.clearToolCalls(sessionKey);
        resetSharedAgentTurnState(sessionKey, event.runId);
        state.addMessage(sessionKey, {
          role: 'system',
          content: toFriendlyErrorMessage(errorText, 'chat-stream'),
          timestamp: new Date().toISOString(),
        });
        return;
      }

      if (event.state === 'aborted') {
        state.setStreamingMessage(sessionKey, null);
        state.setThinking(sessionKey, false);
        state.setThinkingContent(sessionKey, null);
        state.clearToolCalls(sessionKey);
        resetSharedAgentTurnState(sessionKey, event.runId);
      }
    },
    [fetchLastAssistantMessage, fetchThinkingForLastMessage, fetchContextFill, refreshSessions],
  );

  const handleAgentEvent = useCallback((event: AgentEvent) => {
    handleSharedAgentEvent(event);
  }, []);

  const refreshAgentsList = useCallback(async (
    client: GatewayClient,
    options: { clearOnError?: boolean } = {},
  ): Promise<string> => {
    let allAgents: AgentInfo[] = [];
    let defaultAgentId = 'main';

    try {
      const response = await client.listAgents();
      if (clientRef.current !== client) {
        return useAppStore.getState().selectedAgentId ?? defaultAgentId;
      }

      allAgents = Array.isArray(response?.agents) ? response.agents : [];
      defaultAgentId =
        typeof response?.defaultId === 'string' && response.defaultId.trim().length > 0
          ? response.defaultId
          : 'main';

      const latestState = useAppStore.getState();
      latestState.setAgents(allAgents);

      let effectiveAllowedAgents = latestState.allowedAgents;
      if (
        latestState.userRole === 'member'
        && effectiveAllowedAgents.includes('tuntun-builder')
      ) {
        const tuntunAgents = allAgents
          .map((agent) => agent.id)
          .filter((id) => id.startsWith('tuntun-'));
        const mergedAllowedAgents = Array.from(new Set([
          ...effectiveAllowedAgents,
          ...tuntunAgents,
        ]));
        if (mergedAllowedAgents.length !== effectiveAllowedAgents.length) {
          latestState.setAllowedAgents(mergedAllowedAgents);
          effectiveAllowedAgents = mergedAllowedAgents;
        }
      }

      const selectedAgentId = normalizeSelectedAgentId(
        allAgents,
        latestState.selectedAgentId,
        defaultAgentId,
        effectiveAllowedAgents,
      );
      latestState.setSelectedAgentId(selectedAgentId);
      return selectedAgentId;
    } catch (error) {
      if (clientRef.current !== client) {
        return useAppStore.getState().selectedAgentId ?? defaultAgentId;
      }

      console.warn('[useGateway] agents.list failed; agent picker refresh skipped:', error);
      if (options.clearOnError) {
        useAppStore.getState().setAgents([]);
      }
      return useAppStore.getState().selectedAgentId ?? defaultAgentId;
    }
  }, []);

  const connect = useCallback(
    (url: string, token: string) => {
      const state = useAppStore.getState();
      persistServerOriginFromGatewayUrl(url);
      const proxiedUrl = resolvePluginGatewayUrl(url);
      const reconnecting =
        state.hasConnectedOnce ||
        state.reconnectStatus !== 'idle' ||
        reconnectAttemptRef.current > 0 ||
        state.connectionError != null;

      manualDisconnectRef.current = false;
      clearSuccessBannerTimer();

      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }

      verboseEnabledSessionsRef.current.clear();
      reasoningEnabledSessionsRef.current.clear();

      state.setGatewayUrl(proxiedUrl);
      state.setGatewayToken(token);
      state.setConnecting(true);
      state.setConnected(false);
      state.setConnectionError(null);

      if (reconnecting) {
        state.setReconnectState({
          reconnectStatus: 'retrying',
          reconnectAttempt: reconnectAttemptRef.current,
          nextReconnectAt: null,
          reconnectMessage: lastConnectionIssueRef.current ?? 'Connection lost. Trying to reconnect…',
        });
      }

      const client = new GatewayClient({
        url: proxiedUrl,
        token,
        scopes: ['operator.read', 'operator.write', 'operator.admin'],
        clientId: 'clawletai',
        signDevice: async (ctx: SignDeviceContext) => {
          const device = await getOrCreateDevice();
          return signChallenge(device, ctx.nonce, {
            clientId: ctx.clientId,
            clientMode: ctx.clientMode,
            role: ctx.role,
            scopes: ctx.scopes,
            token: ctx.token,
          });
        },
        onConnected: (payload) => {
          if (clientRef.current !== client) {
            return;
          }

          const latestState = useAppStore.getState();
          const wasReconnect = latestState.hasConnectedOnce || latestState.reconnectStatus !== 'idle';

          reconnectAttemptRef.current = 0;
          lastConnectionIssueRef.current = null;

          latestState.setConnected(true);
          latestState.setConnecting(false);
          latestState.setHasConnectedOnce(true);
          latestState.setConnectionError(null);

          if (wasReconnect) {
            latestState.resetTransientUiState();
            latestState.setReconnectState({
              reconnectStatus: 'success',
              reconnectAttempt: 0,
              nextReconnectAt: null,
              reconnectMessage: 'Connected!',
            });
            clearSuccessBannerTimer();
            successBannerTimerRef.current = setTimeout(() => {
              useAppStore.getState().resetReconnectState();
              successBannerTimerRef.current = null;
            }, SUCCESS_BANNER_MS);
            resetAllSharedAgentTurnState();
          } else {
            useAppStore.setState({
              streamingMessage: {},
              thinking: {},
              thinkingContent: {},
              toolCalls: {},
            });
            latestState.resetReconnectState();
          }

          // Reset verbose/reasoning tracking on every (re)connection.
          // Server-side gateway state is fresh on each new WebSocket — refs from
          // a prior connection would cause enableSessionVerbose to skip re-sending
          // sessions.patch with reasoningLevel: 'stream'.
          verboseEnabledSessionsRef.current.clear();
          reasoningEnabledSessionsRef.current.clear();

          // Persist device token for future reconnects
          const deviceToken = payload?.auth?.deviceToken;
          if (deviceToken && typeof deviceToken === 'string') {
            saveDeviceToken(deviceToken);
          }

          void (async () => {
            try {
              const selectedAgentId = await refreshAgentsList(client, { clearOnError: true });

              const sessions = await client.listSessions();
              console.debug('[useGateway] sessions.list returned', sessions.length, 'sessions');
              latestState.setSessions(sessions);

              // Reconcile subagent status based on active sessions
              const activeSessionKeys = sessions
                .map((s) => sessionKeyFromSession(s))
                .filter((k): k is string => k !== null);
              const sessionUpdates: Record<string, number> = {};
              for (const session of sessions) {
                const key = sessionKeyFromSession(session);
                if (key && session.updatedAt) {
                  sessionUpdates[key] = new Date(session.updatedAt).getTime();
                }
              }
              latestState.reconcileSubagents(activeSessionKeys, sessionUpdates);

              const fallbackMainKey = mainSessionKeyForAgent(selectedAgentId);
              const activeSessionKey = useAppStore.getState().activeSessionKey;

              const activeIsInList = activeSessionKey
                ? sessions.some((session) => sessionKeyFromSession(session) === activeSessionKey)
                : false;

              const activeMatchesSelectedAgent =
                activeSessionKey != null &&
                agentIdFromSessionKey(activeSessionKey) === selectedAgentId;

              const key =
                activeMatchesSelectedAgent && (activeIsInList || activeSessionKey === fallbackMainKey)
                  ? (activeSessionKey ?? fallbackMainKey)
                  : (pickPreferredSessionKeyForAgent(sessions, selectedAgentId) ?? fallbackMainKey);

              console.debug('[useGateway] activeSessionKey:', key);
              // Pre-set loading flag before setting active key to prevent welcome screen flash
              useAppStore.getState().setHistoryLoading(key, true);
              latestState.setActiveSessionKey(key);

              if (wasReconnect) {
                // On WS auto-reconnect (same page, GatewayClient internal retry),
                // preserve existing in-memory messages to avoid content-based dedup
                // failures. Voice-relay wraps user messages with timestamp metadata
                // (e.g. "[Tue ... PDT] hi") that the frontend store doesn't have,
                // so a merge would produce duplicates. If there are no messages
                // (e.g. session was reset), fall back to a forced server fetch.
                const existingMsgs = useAppStore.getState().messages[key];
                if (existingMsgs && existingMsgs.length > 0) {
                  // Already have in-memory messages — just clear loading state.
                  useAppStore.getState().setHistoryLoading(key, false);
                  void catchUpHistoryForSession(key);
                } else {
                  // No in-memory messages — load fresh history from server.
                  await loadHistoryForSession(key, true);
                }
              } else {
                // First connect (browser refresh or new page load): always fetch
                // fresh history from the server. This ensures the chat shows the
                // correct, up-to-date message history even if sessionStorage is
                // stale, empty, or was never populated (e.g. tab reopened after
                // close, private browsing, sessionStorage size limit hit).
                await loadHistoryForSession(key, true);
              }

              // Populate context fill on page load so the status bar shows
              // actual context window size, not cumulative totalTokens.
              void fetchContextFill(key);
            } catch (error) {
              console.error('[useGateway] Post-connect error:', error);
              latestState.setConnectionError(toFriendlyErrorMessage(error, 'chat-history'));
            }
          })();
        },
        onChatEvent: handleChatEvent,
        onAgentEvent: handleAgentEvent,
        onEvent: (event) => {
          const state = useAppStore.getState();

          if (event.event === 'reset_confirm_required') {
            const payload = event.payload as Partial<ResetConfirmRequiredPayload>;
            if (
              typeof payload?.nonce === 'string' &&
              typeof payload?.sessionKey === 'string' &&
              typeof payload?.reason === 'string' &&
              typeof payload?.expiresAt === 'number'
            ) {
              // Only show the confirmation dialog if the event belongs to the active session.
              // The gateway broadcasts reset_confirm_required to all connected clients, so we
              // must filter by sessionKey to avoid showing popups for other apps (e.g. Muta).
              if (payload.sessionKey !== state.activeSessionKey) {
                return;
              }
              state.enqueueResetConfirm({
                nonce: payload.nonce,
                sessionKey: payload.sessionKey,
                reason: payload.reason,
                senderId: typeof payload.senderId === 'string' ? payload.senderId : undefined,
                source: typeof payload.source === 'string' ? payload.source : undefined,
                expiresAt: payload.expiresAt,
              });
            }
            return;
          }

          if (event.event === 'reset_cancelled') {
            const nonce = typeof event.payload?.nonce === 'string' ? event.payload.nonce : null;
            if (nonce) {
              state.removeResetConfirm(nonce);
            }
            return;
          }

          if (event.event === 'reset_applied') {
            const nonce = typeof event.payload?.nonce === 'string' ? event.payload.nonce : null;
            const sessionKey = typeof event.payload?.sessionKey === 'string' ? event.payload.sessionKey : null;
            if (nonce) {
              state.removeResetConfirm(nonce);
            }
            if (sessionKey) {
              resetSharedAgentTurnState(sessionKey);
              state.clearMessages(sessionKey);
              state.clearPendingAttachments();
              state.startSessionReset(sessionKey);
              verboseEnabledSessionsRef.current.delete(sessionKey);
              reasoningEnabledSessionsRef.current.delete(sessionKey);
              void enableSessionVerbose(sessionKey);
            }
            return;
          }

          if (event.event === 'sessions.updated') {
            // Re-fetch sessions list to pick up title changes
            void client.listSessions().then((sessions) => {
              useAppStore.getState().setSessions(sessions);
            }).catch((err) => {
              console.warn('[useGateway] sessions.updated refresh failed:', err);
            });
            return;
          }
        },
        onDisconnected: (reason) => {
          if (clientRef.current !== client) {
            return;
          }

          const latestState = useAppStore.getState();
          latestState.setConnected(false);
          latestState.setConnecting(false);

          if (manualDisconnectRef.current) {
            latestState.resetReconnectState();
            latestState.setConnectionError(null);
            reconnectAttemptRef.current = 0;
            lastConnectionIssueRef.current = null;
            return;
          }

          const message = lastConnectionIssueRef.current ?? friendlyDisconnectMessage(reason);
          latestState.setConnectionError(message);
          showRetryBanner(message);
        },
        onError: (error) => {
          if (clientRef.current !== client) {
            return;
          }

          const latestState = useAppStore.getState();
          latestState.setConnecting(false);

          // Check for PAIRING_REQUIRED — attempt auto-approve with stored invite token
          if (error instanceof GatewayError && error.details?.code === 'PAIRING_REQUIRED') {
            const requestId = error.details.requestId;
            if (requestId) {
              console.debug('[useGateway] Pairing required, attempting auto-approve...');
              void attemptAutoApprove(requestId).then((approved) => {
                if (approved) {
                  // Reconnect after short delay to let gateway process the approval
                  setTimeout(() => {
                    const gwUrl = useAppStore.getState().gatewayUrl;
                    const gwToken = useAppStore.getState().gatewayToken;
                    if (gwUrl && gwToken) {
                      connect(gwUrl, gwToken);
                    }
                  }, 500);
                  return; // Don't show error to user
                }

                const friendlyMessage = 'This device needs permission before it can connect again.';
                lastConnectionIssueRef.current = friendlyMessage;
                latestState.setConnectionError(friendlyMessage);
                latestState.setReconnectState({
                  reconnectStatus: 'retrying',
                  reconnectMessage: friendlyMessage,
                });
              });
              return; // Don't set error yet — wait for auto-approve result
            }
          }

          const friendlyMessage = friendlyConnectionMessage(error);
          lastConnectionIssueRef.current = friendlyMessage;
          latestState.setConnectionError(friendlyMessage);
          latestState.setReconnectState({
            reconnectStatus: 'retrying',
            reconnectMessage: friendlyMessage,
          });
        },
      });

      // Restore persisted device token for reconnection
      const existingDeviceToken = loadDeviceToken();
      if (existingDeviceToken) {
        client.setDeviceToken(existingDeviceToken);
      }

      clientRef.current = client;
      gatewayRef.current = client;
      client.connect();
    },
    [
      catchUpHistoryForSession,
      clearSuccessBannerTimer,
      handleAgentEvent,
      handleChatEvent,
      loadHistoryForSession,
      refreshAgentsList,
      showRetryBanner,
    ],
  );

  useEffect(() => {
    const enabled = shouldAutoRefreshAgentList({ connected: autoRefreshConnected, userRole, allowedAgents });
    if (!enabled) {
      return undefined;
    }

    const refresh = () => {
      const client = clientRef.current;
      if (!client || !client.connected) {
        return;
      }
      void refreshAgentsList(client);
    };

    refresh();
    const intervalID = window.setInterval(refresh, AGENT_LIST_REFRESH_INTERVAL_MS);
    window.addEventListener('focus', refresh);

    return () => {
      window.clearInterval(intervalID);
      window.removeEventListener('focus', refresh);
    };
  }, [allowedAgents, autoRefreshConnected, refreshAgentsList, userRole]);

  useEffect(() => {
    if (!autoRefreshConnected) {
      return undefined;
    }

    const catchUpActiveSession = () => {
      const key = useAppStore.getState().activeSessionKey;
      if (!key) {
        return;
      }
      void catchUpHistoryForSession(key);
    };

    const catchUpWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        catchUpActiveSession();
      }
    };

    const intervalID = window.setInterval(catchUpActiveSession, ACTIVE_SESSION_CATCH_UP_INTERVAL_MS);
    window.addEventListener('focus', catchUpActiveSession);
    document.addEventListener('visibilitychange', catchUpWhenVisible);

    return () => {
      window.clearInterval(intervalID);
      window.removeEventListener('focus', catchUpActiveSession);
      document.removeEventListener('visibilitychange', catchUpWhenVisible);
    };
  }, [autoRefreshConnected, catchUpHistoryForSession]);

  const disconnect = useCallback(() => {
    manualDisconnectRef.current = true;
    reconnectAttemptRef.current = 0;
    lastConnectionIssueRef.current = null;
    clearSuccessBannerTimer();

    const client = clientRef.current;
    clientRef.current = null;
    gatewayRef.current = null;

    if (client) {
      client.disconnect();
    }

    verboseEnabledSessionsRef.current.clear();
    reasoningEnabledSessionsRef.current.clear();

    useAppStore.setState((state) => ({
      ...state,
      connected: false,
      connecting: false,
      hasConnectedOnce: false,
      connectionError: null,
      reconnectStatus: 'idle',
      reconnectAttempt: 0,
      nextReconnectAt: null,
      reconnectMessage: null,
      sessions: [],
      agents: [],
      activeSessionKey: null,
      messages: {},
      streamingMessage: {},
      thinking: {},
      thinkingContent: {},
      toolCalls: {},
      pendingAttachments: [],
      sessionImageCount: {},
      resetConfirmQueue: [],
    }));
  }, [clearSuccessBannerTimer]);

  const reconnect = useCallback(() => {
    const state = useAppStore.getState();
    if (!state.gatewayUrl || !state.gatewayToken) {
      return;
    }

    reconnectAttemptRef.current = 0;
    clearSuccessBannerTimer();
    state.setReconnectState({
      reconnectStatus: 'retrying',
      reconnectAttempt: 0,
      nextReconnectAt: null,
      reconnectMessage: lastConnectionIssueRef.current ?? 'Connection lost. Trying to reconnect…',
    });
    connect(state.gatewayUrl, state.gatewayToken);
  }, [clearSuccessBannerTimer, connect]);

  const sendMessage = useCallback(async (text: string, attachments?: Attachment[]) => {
    const client = clientRef.current;
    const state = useAppStore.getState();
    const sessionKey = state.activeSessionKey;

    if (!client || !client.connected) {
      throw new Error('Not connected to gateway');
    }

    if (!sessionKey) {
      throw new Error('No active session selected');
    }

    const content = text.trim();
    const hasAttachments = Boolean(attachments && attachments.length > 0);

    // Allow empty text ONLY if there are attachments.
    if (!content && !hasAttachments) {
      return;
    }

    const isFamilySession = sessionKey === 'agent:yuki:family';
    const displayName = useSettingsStore.getState().settings.general.displayName.trim();
    const messageToSend = isFamilySession && displayName ? `[${displayName}] ${content}` : content;

    state.clearRunState(sessionKey);

    try {
      await client.sendMessage(
        sessionKey,
        messageToSend || '',
        hasAttachments ? attachments : undefined,
      );
    } catch (error) {
      console.error('[useGateway] Failed to send message:', error);
      state.addMessage(sessionKey, {
        role: 'system',
        content: toFriendlyErrorMessage(error, 'chat-send'),
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }, []);

  const sendBtwMessage = useCallback(async (text: string): Promise<{ sessionKey: string; label: string }> => {
    const state = useAppStore.getState();
    const sessionKey = state.activeSessionKey;
    const gatewayToken = state.gatewayToken;

    if (!sessionKey) {
      throw new Error('No active session selected');
    }

    const response = await fetch(buildApiUrl('/api/btw', state.gatewayUrl), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${gatewayToken}`,
      },
      body: JSON.stringify({ sessionKey, text }),
    });

    const data = (await response.json().catch(() => ({}))) as {
      sessionKey?: unknown;
      label?: unknown;
      error?: unknown;
    };

    if (!response.ok) {
      const message = typeof data.error === 'string' ? data.error : 'Failed to start /btw';
      throw new Error(message);
    }

    if (typeof data.sessionKey !== 'string' || !data.sessionKey) {
      throw new Error('Plugin returned an invalid /btw session key');
    }

    return {
      sessionKey: data.sessionKey,
      label: typeof data.label === 'string' && data.label.trim() ? data.label : 'btw',
    };
  }, []);

  /**
   * Send a message to a session without showing it in the chat UI.
   * Used for silent triggers (e.g., invite greeting to Yuki).
   */
  const sendSilentMessage = useCallback(async (sessionKey: string, text: string) => {
    const client = clientRef.current;
    if (!client?.connected || !sessionKey || !text.trim()) return;
    try {
      await client.sendMessage(sessionKey, text.trim());
    } catch (e) {
      console.warn('[useGateway] Silent message failed:', e);
    }
  }, []);

  /** Clear cached verbose/reasoning flags for a session and re-send sessions.patch.
   *  Call after `/new` or `/reset` so the gateway gets `reasoningLevel: 'stream'`
   *  on the freshly-created session. */
  const reEnableSessionConfig = useCallback(
    async (sessionKey: string) => {
      verboseEnabledSessionsRef.current.delete(sessionKey);
      reasoningEnabledSessionsRef.current.delete(sessionKey);
      await enableSessionVerbose(sessionKey);
    },
    [enableSessionVerbose],
  );

  /** Load transcript for a subagent session (for completed subagents with no streaming text) */
  const loadSubagentHistory = useCallback(async (sessionKey: string): Promise<string | null> => {
    const client = clientRef.current;
    if (!client || !client.connected || !sessionKey) {
      return null;
    }

    try {
      const { messages } = await client.getHistory(sessionKey, 100);
      const scopedMessages = messages.filter((msg) => {
        const messageSessionKey = typeof msg?.sessionKey === 'string' ? msg.sessionKey : null;
        return !messageSessionKey || messageSessionKey === sessionKey;
      });
      const transcript = scopedMessages
        .map((msg) => {
          const role = String(msg.role ?? 'unknown');
          const title = role.charAt(0).toUpperCase() + role.slice(1);
          const text = extractTextContent(msg.content).trim();
          return text ? `### ${title}\n\n${text}` : null;
        })
        .filter((line): line is string => line !== null)
        .join('\n\n---\n\n');
      return transcript || null;
    } catch (error) {
      console.warn('[useGateway] Failed to load subagent history:', error);
      return null;
    }
  }, []);

  /** Abort a chat session. Returns the abort response or throws on failure. */
  const abortChat = useCallback(async (sessionKey: string): Promise<{ ok: boolean; status?: string }> => {
    const client = clientRef.current;
    if (!client || !client.connected || !sessionKey) {
      throw new Error('Gateway is not connected');
    }

    const res = await client.abortChat(sessionKey);
    return res;
  }, []);

  /** Force-kill a subagent session and remove it from the active session list. */
  const killSubagentSession = useCallback(async (sessionKey: string): Promise<boolean> => {
    const client = clientRef.current;
    if (!client || !client.connected || !sessionKey) {
      return false;
    }

    try {
      await client.request('sessions.delete', {
        key: sessionKey,
        deleteTranscript: false,
      });
      return true;
    } catch (deleteError) {
      try {
        await client.request('sessions.destroy', {
          key: sessionKey,
          deleteTranscript: false,
        });
        return true;
      } catch (destroyError) {
        console.warn('[useGateway] Failed to kill subagent session:', destroyError ?? deleteError);
        return false;
      }
    }
  }, []);

  /** Fetch full session history from gateway and format as a complete transcript.
   *  Unlike toHistoryChatMessage (which drops tool/tool_result roles and truncates),
   *  this preserves ALL message roles and content for maximum context fidelity. */
  const getFullSessionTranscript = useCallback(async (sessionKey: string): Promise<string | null> => {
    const client = clientRef.current;
    if (!client || !client.connected || !sessionKey) return null;

    try {
      const { messages } = await client.getHistory(sessionKey, 1000);
      if (!messages || messages.length === 0) return null;

      const parts: string[] = [];
      for (const msg of messages) {
        const role = String(msg.role ?? 'unknown');

        // Skip system messages (bootstrap/injected context)
        if (role === 'system') continue;

        if (role === 'user') {
          const text = extractTextContent(msg.content).trim();
          if (text) parts.push(`**User:**\n${text}`);
        } else if (role === 'assistant') {
          const text = extractTextContent(msg.content).trim();
          let entry = text ? `**Assistant:**\n${text}` : '';

          // Include tool_use blocks from structured content
          if (Array.isArray(msg.content)) {
            const toolUses = (msg.content as any[]).filter(
              (b: any) => b?.type === 'tool_use',
            );
            if (toolUses.length > 0) {
              const summaries = toolUses.map((tu: any) => {
                const params = JSON.stringify(tu.input ?? {});
                return ` - ${tu.name} params: ${params}`;
              });
              entry += `\n\nTool calls:\n${summaries.join('\n')}`;
            }
          }

          if (entry) parts.push(entry);
        } else if (role === 'tool' || role === 'tool_result' || role === 'toolResult') {
          // Include tool results — these are dropped by toHistoryChatMessage
          const resultText = extractTextContent(msg.content).trim();
          if (resultText) {
            parts.push(`Tool result:\n${resultText}`);
          }
        }
      }

      return parts.length > 0 ? parts.join('\n\n---\n\n') : null;
    } catch (error) {
      console.warn('[useGateway] Failed to get full session transcript:', error);
      return null;
    }
  }, []);

  const connected = useAppStore((state) => state.connected);
  const connecting = useAppStore((state) => state.connecting);
  const agents = useAppStore((state) => state.agents);

  return {
    connect,
    reconnect,
    disconnect,
    sendMessage,
    sendBtwMessage,
    sendSilentMessage,
    refreshSessions,
    loadHistoryForSession,
    reEnableSessionConfig,
    loadSubagentHistory,
    getFullSessionTranscript,
    abortChat,
    killSubagentSession,
    connected,
    connecting,
    agents,
  };
}
